import { omitBy, isNull, isEmpty, isNil } from "lodash";
import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useMemo } from "react";
import { Moment } from "moment";
import errorHandler from "Utils/error-handler";
import { ServicePointSchema } from "./service-point-form-validation";
import useQuery from "Hooks/query.hook";
import useStatesList from "Hooks/states-option.hook";
import "../delivery-forms.scss";
import { Day } from "Types/common.types";
import { Col, Divider, Row, Checkbox } from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import {
  AlertBox,
  Button,
  TextInput,
  SelectInput,
  TimePicker,
} from "@life-line-lab/components-react";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import moment from "moment";

interface ServicePointFormProps {
  onSubmit: (data: any) => void;
  onUpdate: (data: any) => void;
  onFetch: (pointID: string) => Promise<void>;
  handleFetchLines: () => Promise<void>;
  handleFetchPointTypes: () => Promise<void>;
  data?: any;
  settings?: any;
}

const initialWorkingHours = [
  {
    day: "MONDAY",
    closingTime: null,
    openingTime: null,
    isChecked: false,
  },
  {
    day: "TUESDAY",
    closingTime: null,
    openingTime: null,
    isChecked: false,
  },
  {
    day: "WEDNESDAY",
    closingTime: null,
    openingTime: null,
    isChecked: false,
  },
  {
    day: "THURSDAY",
    closingTime: null,
    openingTime: null,
    isChecked: false,
  },
  {
    day: "FRIDAY",
    closingTime: null,
    openingTime: null,
    isChecked: false,
  },
  {
    day: "SATURDAY",
    closingTime: null,
    openingTime: null,
    isChecked: false,
  },
  {
    day: "SUNDAY",
    closingTime: null,
    openingTime: null,
    isChecked: false,
  },
];

const useDaysChecked = (input, setInput, error, setError) => {
  const modifyState = (currentInput) => {
    setInput((prevInput) => ({ ...prevInput, workingHours: currentInput }));
  };

  const setDays = (e, day) => {
    setError({ ...error, workingHours: null });
    const dayIsChecked = e.target.checked;

    //  if the everyday checkbox is checked, set all isCheck to true
    // ================================================================
    if (day == Day.everyday) {
      if (dayIsChecked) {
        let workingHours = input?.workingHours?.map((current) => {
          return {
            ...current,
            isChecked: true,
          };
        });

        modifyState(workingHours);
      } else {
        //  if the everyday checkbox is unchecked, set all isCheck to false and clear time
        // =================================================================================
        let workingHours = input?.workingHours?.map((current) => {
          return {
            ...current,
            openingTime: null,
            closingTime: null,
            isChecked: false,
          };
        });

        modifyState(workingHours);
      }
    } else {
      //  if a day is checked, set that day's isCheck to true
      // =========================================================================
      if (dayIsChecked) {
        let workingHours = input?.workingHours?.map((current) => {
          if (day == current?.day) {
            return {
              ...current,
              isChecked: true,
            };
          } else {
            return current;
          }
        });

        modifyState(workingHours);
      } else if (!dayIsChecked) {
        //  if a day is unchecked, set that day's isCheck to false and clear time
        // =========================================================================
        let workingHours = input?.workingHours?.map((current) => {
          if (day == current?.day) {
            return {
              ...current,
              openingTime: null,
              closingTime: null,
              isChecked: false,
            };
          } else {
            return current;
          }
        });

        modifyState(workingHours);
      }
    }
  };

  return {
    setDays,
  };
};

const useFormHandlers = (props) => {
  const {
    input,
    error,
    setDays,
    setIndeterminate,
    setIsAllSelected,
    setInput,
    setError,
  } = props || {};

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInput({ ...input, [e.target.name]: e.target.value });
    setError({ ...error, [e.target.name]: null });
  };

  const handleSelectChange = (value: string | string[], name: string) => {
    setError({ ...error, [name]: null });
    switch (name) {
      case "country": {
        setInput({ ...input, [name]: value as string, state: undefined });
        break;
      }
      case "lineIDs":
        setInput({ ...input, [name]: value as string, stationCodes: undefined });
        break;
      default: {
        setInput({ ...input, [name]: value });
        break;
      }
    }
  };

  const handleTimeChange = (
    time: Moment | null,
    day: Day,
    type: "openingTime" | "closingTime",
  ) => {
    // clear error
    setError({ ...error, workingHours: null });

    // sets the time in which the day is checked
    // if openingTime is passed, set openingTime, otherwise set closingTime
    // when one is set, the other takes either existing value or null
    setInput((prevInput) => ({
      ...prevInput,
      workingHours: prevInput?.workingHours?.map((current) => {
        if (current?.day == day) {
          return {
            ...current,
            openingTime:
              type == "openingTime" ? time : current?.openingTime ?? null,
            closingTime:
              type == "closingTime" ? time : current?.closingTime ?? null,
          };
        } else {
          return current;
        }
      }),
    }));
  };

  const handleWorkingHourChange = (e: CheckboxChangeEvent, day: Day) => {
    let numberOfChecks = 0;

    // count the number of checked boxes
    // 7 because there are 7 possible working days (Monday -> Sunday)
    for (let i = 0; i < 7; i++) {
      if (input?.workingHours && input?.workingHours[i]?.isChecked) {
        numberOfChecks++;
      }
    }

    // if the day is checked, but not select all, add into numberOfChecks
    if (day !== Day.everyday && e.target.checked) {
      numberOfChecks++;
    }

    // if selectAll is checked by user OR numberOfChecks = 7, check the selectAll box
    if (day === Day.everyday || numberOfChecks == 7) {
      setIndeterminate && setIndeterminate(false);

      if (e.target.checked) setIsAllSelected && setIsAllSelected(true);
      else setIsAllSelected && setIsAllSelected(false);
    } else {
      // assume at least one is checked (cant be 7 because that is handled in the above if)
      setIndeterminate && setIndeterminate(true);
      // unset indeterminate if numberOfChecks is 0
      if (numberOfChecks === 0) setIndeterminate && setIndeterminate(false);
    }

    setDays && setDays(e, day);
  };

  return {
    handleFieldChange,
    handleTimeChange,
    handleSelectChange,
    handleWorkingHourChange,
  };
};

const OperationalHoursComponent = (props) => {
  const {
    indeterminate,
    isAllSelected,
    input,
    error,
    handleWorkingHourChange,
    handleTimeChange,
  } = props || {};
  const { t } = useTranslation();

  return (
    <div className="oh-container">
      <div>
        <label className="inp-label inp-label--required">
          {t("label.operationalHours")}
        </label>
      </div>
      <div className="oh-container-inner">
        <Row gutter={[0, 16]}>
          <Col span={24}>
            <div className="oh-select-all">
              <Checkbox
                indeterminate={indeterminate}
                checked={isAllSelected}
                onChange={(e) => handleWorkingHourChange(e, "everyday" as Day)}
              >
                {t(`label.selectAll`)}
              </Checkbox>
              {isAllSelected && (
                <div className="cm-info cm-help">
                  <ExclamationCircleOutlined style={{ fontSize: "16px" }} />
                  <div>{t("txt.setOperationalHoursEveryDay")}</div>
                </div>
              )}
            </div>
          </Col>
          <div className="oh-divider"></div>
          <Col span={24}>
            <Row gutter={[32, 0]}>
              <AlertBox text={error.workingHours} />
              <Col span={12}>
                <Row gutter={[16, 16]} align="middle">
                  {input?.workingHours?.map((current, index) => {
                    if (index < 4)
                      return (
                        <>
                          <Col span={8}>
                            <Checkbox
                              checked={current?.isChecked}
                              onChange={(e) =>
                                handleWorkingHourChange(e, current?.day as Day)
                              }
                            >
                              {t(`label.${current?.day?.toUpperCase()}`)}
                            </Checkbox>
                          </Col>
                          <Col span={8}>
                            <TimePicker
                              format={"hh:mm a"}
                              placeholder={t("txt.openingHours")}
                              value={current?.openingTime}
                              onChange={(time) => {
                                handleTimeChange(
                                  time,
                                  current?.day as Day,
                                  "openingTime",
                                );
                              }}
                            />
                          </Col>
                          <Col span={8}>
                            <TimePicker
                              format={"hh:mm a"}
                              placeholder={t("txt.closingHours")}
                              value={current?.closingTime}
                              onChange={(time) => {
                                handleTimeChange(
                                  time,
                                  current?.day as Day,
                                  "closingTime",
                                );
                              }}
                            />
                          </Col>
                        </>
                      );
                  })}
                </Row>
              </Col>
              <Col span={12}>
                <Row gutter={[16, 16]} align="middle">
                  {input?.workingHours?.map((current, index) => {
                    if (index >= 4)
                      return (
                        <>
                          <Col span={8}>
                            <Checkbox
                              checked={current?.isChecked}
                              onChange={(e) =>
                                handleWorkingHourChange(e, current?.day as Day)
                              }
                            >
                              {t(`label.${current?.day?.toUpperCase()}`)}
                            </Checkbox>
                          </Col>
                          <Col span={8}>
                            <TimePicker
                              format={"hh:mm a"}
                              placeholder={t("txt.openingHours")}
                              value={current?.openingTime}
                              onChange={(time) => {
                                handleTimeChange(
                                  time,
                                  current?.day as Day,
                                  "openingTime",
                                );
                              }}
                            />
                          </Col>
                          <Col span={8}>
                            <TimePicker
                              format={"hh:mm a"}
                              placeholder={t("txt.closingHours")}
                              value={current?.closingTime}
                              onChange={(time) => {
                                handleTimeChange(
                                  time,
                                  current?.day as Day,
                                  "closingTime",
                                );
                              }}
                            />
                          </Col>
                        </>
                      );
                  })}
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    </div>
  );
};

const ServicePointForm: React.FC<ServicePointFormProps> = ({
  onSubmit,
  onUpdate,
  onFetch,
  handleFetchLines,
  handleFetchPointTypes,
  data,
  settings,
}) => {
  //TODO: Enhancement: This code assumes that all lines are in malaysia, but in the future,
  // there will be other countries. Make changes accordingly
  const query = useQuery();
  const id = query.get("id");
  const { t } = useTranslation();

  const initialInput = {
    lineIDs: [],
    pointTypeID: [],
    stationCodes: [],
    locationName: "",
    street1: "",
    street2: "",
    postcode: "",
    city: "",
    state: null,
    workingHours: [...initialWorkingHours],
  };
  const initialError = {};

  const [input, setInput] = useState<any>(initialInput);
  const [error, setError] = useState<any>(initialError);
  const [indeterminate, setIndeterminate] = useState<boolean>(false);
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
  const stateOption = useStatesList("MY");

  const { setDays } = useDaysChecked(input, setInput, error, setError);

  const formHandlerProps = {
    input,
    error,
    setDays,
    setIndeterminate,
    setIsAllSelected,
    setInput,
    setError,
  };

  const {
    handleFieldChange,
    handleWorkingHourChange,
    handleTimeChange,
    handleSelectChange,
  } = useFormHandlers({ ...formHandlerProps });

  const stations = useMemo(() => {
    if (!input.lineIDs) return [];

    const selectedLines: any[] = settings.LINE?.filter((line: any) =>
      input.lineIDs.includes(line._id),
    );

    // Find groups that have at least one selected option
    const selectedGroups = new Set(
      selectedLines?.flatMap((line) =>
        line.stations.some((station: any) =>
          input.stationCodes?.includes(station.stationCode),
        )
          ? [line.name] // If any station is selected, store the group (line name)
          : [],
      ),
    );

    const structureGroupedOptions = selectedLines?.map((line) => ({
      label: line.name,
      options: line.stations.map((station: any) => ({
        label: station.stationName,
        value: station.stationCode,
        disabled: input.stationCodes?.includes(station.stationCode)
          ? false // Keep selected items enabled for removal
          : selectedGroups.has(line.name),
      })),
    }));

    return structureGroupedOptions ?? [];
  }, [input.lineIDs, settings, input.stationCodes]);

  const handleSubmit = () => {
    try {
      const valInput = {
        ...input,
        address: omitBy(
          {
            street1: input.street1,
            street2: input.street2,
            postcode: input.postcode,
            city: input.city,
            state: input.state,
            country: "MY",
          },
          isEmpty,
        ),
        street1: null,
        street2: null,
        postcode: null,
        city: null,
        state: null,
        country: null,
      };

      const value = ServicePointSchema.validateSync(omitBy(valInput, isNil), {
        strict: true,
        abortEarly: true,
        stripUnknown: false,
      });
      id ? onUpdate(value) : onSubmit(value);
    } catch (err) {
      console.log(err);
      const { path, value }: any = errorHandler.validation(err);
      setError({
        ...error,
        [path]: t(`${value}`, { field: t(`label.${path}`) }),
      });
    }
  };

  useEffect(() => {
    const fetchResponses = async () => {
      if (id) {
        await onFetch(id);
      }

      await handleFetchLines();
      await handleFetchPointTypes();
    };

    fetchResponses();
    document.body.classList.add("stack-screen--open");
    return () => document.body.classList.remove("stack-screen--open");
  }, []);

  useEffect(() => {
    if (!isEmpty(data)) {
      let updatedWorkingHours = input.workingHours?.map((hour) => {
        const matchingHour = data?.workingHours?.find(
          (wh) => wh.day === hour.day,
        );
        return matchingHour
          ? {
              day: matchingHour.day,
              openingTime: moment(
                matchingHour.openingTime,
                "YYYY-MM-DD hh:mm a Z",
              ),
              closingTime: moment(
                matchingHour.closingTime,
                "YYYY-MM-DD hh:mm a Z",
              ),
              isChecked: true,
            }
          : hour;
      });

      setInput((prev) => ({
        ...prev,
        workingHours: updatedWorkingHours,
        _id: data._id,
        lineIDs: data.lineIDs,
        stationCodes: data?.stationCodes,
        pointTypeID: data.pointTypeID,
        locationName: data.locationName,
        street1: data.address.street1,
        street2: data.address.street2,
        postcode: data.address.postcode,
        city: data.address.city,
        state: data.address.state,
      }));

      if (data?.workingHours?.length === 7)
        setIsAllSelected && setIsAllSelected(true);
      else if (data?.workingHours?.length > 0)
        setIndeterminate && setIndeterminate(true);
    }
  }, [data]);

  return (
    <div className="delivery-form">
      <form>
        <div className="main-header">
          <span className="center header-title">
            {t(
              id ? "label.editServicePoint" : "label.addServicePoint",
            ).toUpperCase()}
          </span>
        </div>

        <Row gutter={[20, 0]} className="mt-32">
          <Col xs={24} sm={24} md={8} className="mb-30">
            <SelectInput
              showSearch
              isRequired={true}
              label={t("label.line")}
              placeholder={t("txt.selectHere")}
              value={input.lineIDs}
              name="lineIDs"
              options={
                settings.LINE?.map((line) => {
                  return {
                    label: line.name,
                    value: line._id,
                  };
                }) || []
              }
              filterOption={(inputValue, option) =>
                option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >=
                0
              }
              onChange={(value) => handleSelectChange(value, "lineIDs")}
              mode="multiple"
              showArrow={true}
              allowClear
            />
            <AlertBox
              text={
                error.lineID
                  ? t("error.pattern", { field: t("label.line") })
                  : null
              }
            />
          </Col>
          <Col xs={24} sm={24} md={8} className="mb-30">
            <SelectInput
              showSearch
              isRequired={true}
              isReadonly={isNull(input.lineIDs)}
              label={t("label.station")}
              placeholder={t("txt.selectHere")}
              value={input.stationCodes}
              name="stationCode"
              options={stations as any}
              filterOption={(inputValue, option) =>
                option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >=
                0
              }
              onChange={(value) => {
                handleSelectChange(value, "stationCodes");
              }}
              mode="multiple"
              showArrow={true}
              allowClear
            />
            <AlertBox
              text={
                error.lineIDs
                  ? t("error.pattern", { field: t("label.line") })
                  : null
              }
            />
          </Col>
          <Col xs={24} sm={24} md={8} className="mb-30">
            <SelectInput
              showSearch
              isRequired={true}
              label={t("label.type")}
              placeholder={t("txt.selectHere")}
              value={input.pointTypeID}
              name="pointTypeID"
              options={
                settings.SERVICE_POINT_TYPE?.map((type) => {
                  return {
                    label: type.name,
                    value: type._id,
                  };
                }) || []
              }
              filterOption={(inputValue, option) =>
                option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >=
                0
              }
              onChange={(value) => handleSelectChange(value, "pointTypeID")}
              mode="multiple"
              showArrow={true}
              allowClear
            />
            <AlertBox
              text={
                error.pointTypeID
                  ? t("error.pattern", { field: t("label.type") })
                  : null
              }
            />
          </Col>
          <Col xs={24} sm={24} md={8} className="mb-30">
            <TextInput
              isRequired={true}
              label={t("label.locationName")}
              placeholder={t("txt.enterHere")}
              value={input.locationName}
              name="locationName"
              onChange={handleFieldChange}
            />
            <AlertBox text={error.locationName} />
          </Col>
        </Row>

        <Divider className="mb-0 mt-3" />

        <Row gutter={[20, 0]} className="mt-20">
          <Col xs={24} sm={24} md={12} className="mb-10">
            <TextInput
              isRequired={true}
              label={t("label.street1")}
              placeholder={t("txt.enterHere")}
              value={input.street1}
              name="street1"
              onChange={handleFieldChange}
            />
            <AlertBox
              text={
                error["address.street1"]
                  ? t("error.pattern", { field: t("label.street1") })
                  : null
              }
            />
          </Col>
          <Col xs={24} sm={24} md={12} className="mb-10">
            <TextInput
              label={t("label.street2")}
              placeholder={t("txt.enterHere")}
              value={input.street2}
              name="street2"
              onChange={handleFieldChange}
            />
            <AlertBox
              text={
                error["address.street2"]
                  ? t("error.pattern", { field: t("label.street2") })
                  : null
              }
            />
          </Col>
        </Row>
        <Row gutter={[20, 0]} className="mb-20">
          <Col xs={24} sm={24} md={6} className="mb-10">
            <TextInput
              isRequired={true}
              label={t("label.postcode")}
              placeholder={t("txt.enterHere")}
              value={input.postcode}
              name="postcode"
              onChange={handleFieldChange}
            />
            <AlertBox
              text={
                error["address.postcode"]
                  ? t("error.pattern", { field: t("label.postcode") })
                  : null
              }
            />
          </Col>
          <Col xs={24} sm={24} md={6} className="mb-10">
            <TextInput
              isRequired={true}
              label={t("label.city")}
              placeholder={t("txt.enterHere")}
              value={input.city}
              name="city"
              onChange={handleFieldChange}
            />
            <AlertBox
              text={
                error["address.city"]
                  ? t("error.pattern", { field: t("label.city") })
                  : null
              }
            />
          </Col>
          <Col xs={24} sm={24} md={12} className="mb-10">
            <SelectInput
              showSearch
              isRequired={true}
              value={input.state}
              name="state"
              options={stateOption}
              filterOption={(inputValue, option) =>
                option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >=
                0
              }
              label={t("label.state")}
              placeholder={t("txt.selectHere")}
              onChange={(value: string | string[]) =>
                handleSelectChange(value, "state")
              }
            />
            <AlertBox
              text={
                error["address.state"]
                  ? t("error.pattern", { field: t("label.state") })
                  : null
              }
            />
          </Col>
        </Row>
        <Divider className="mb-0 mt-3" />
        <Col xs={24} sm={24} className="mb-10">
          <TextInput
            label={t("label.handlingInstructions")}
            placeholder={t("txt.enterHere")}
            value={input.handlingInstructions}
            name="handlingInstructions"
            onChange={handleFieldChange}
          />
        </Col>
        <Col xs={24} sm={24}>
          <OperationalHoursComponent
            indeterminate={indeterminate}
            isAllSelected={isAllSelected}
            input={input}
            error={error}
            handleWorkingHourChange={handleWorkingHourChange}
            handleTimeChange={handleTimeChange}
          />
        </Col>
        <Row className="mt-50" justify="center">
          <Col className="mb-50">
            <Button
              size="large"
              type="primary"
              onClick={handleSubmit}
              label={id ? t("action.update") : t("action.create")}
            />
          </Col>
        </Row>
      </form>
    </div>
  );
};

export default ServicePointForm;
